import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as HiIcons from 'react-icons/hi';

const { FiTrendingUp, FiTarget, FiZap, FiChevronDown } = FiIcons;
const { HiSparkles } = HiIcons;

const HeroSection = () => {
  const floatingIcons = [
    { icon: FiTrendingUp, delay: 0, duration: 5 },
    { icon: FiTarget, delay: 0.5, duration: 6 },
    { icon: FiZap, delay: 1, duration: 5.5 },
    { icon: HiSparkles, delay: 1.5, duration: 5.2 }
  ];

  const particles = Array.from({ length: 10 }).map((_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 5 + 2,
    duration: Math.random() * 20 + 10
  }));

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br" style={{ background: 'linear-gradient(135deg, #3a1c40 0%, #1e0f21 100%)' }} />

      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full bg-purple-400 opacity-30 will-change-transform"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
            transform: 'translate3d(0,0,0)'
          }}
          animate={{
            x: [0, Math.random() * 200 - 100],
            y: [0, Math.random() * 200 - 100],
            opacity: [0.2, 0.8, 0.2]
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut"
          }}
        />
      ))}

      <div className="absolute inset-0 opacity-20">
        <motion.div 
          className="absolute top-10 left-10 w-32 h-32 border-2 border-purple-400 rotate-45 will-change-transform"
          style={{ transform: 'translate3d(0,0,0)' }}
          animate={{ rotate: [45, 90, 45], scale: [1, 1.1, 1], opacity: [0.5, 0.8, 0.5] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute top-40 right-20 w-24 h-24 border-2 border-yellow-400 rounded-full will-change-transform"
          style={{ transform: 'translate3d(0,0,0)' }}
          animate={{ y: [-20, 20, -20], scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {floatingIcons.map((item, index) => (
        <motion.div
          key={index}
          className="absolute hidden lg:block will-change-transform"
          style={{
            top: `${20 + index * 20}%`,
            right: `${10 + index * 12}%`,
            transform: 'translate3d(0,0,0)'
          }}
          animate={{
            y: [-20, 20, -20],
            x: [0, 15, 0],
            rotate: [0, 10, -10, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: item.duration, delay: item.delay, repeat: Infinity, ease: "easeInOut" }}
        >
          <motion.div 
            className="p-4 bg-purple-600 bg-opacity-30 rounded-full backdrop-blur-sm"
            whileHover={{ scale: 1.2, backgroundColor: "rgba(147, 51, 234, 0.5)" }}
            animate={{
              boxShadow: ["0 0 0 rgba(147, 51, 234, 0)", "0 0 20px rgba(147, 51, 234, 0.7)", "0 0 0 rgba(147, 51, 234, 0)"]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <SafeIcon icon={item.icon} className="text-3xl text-white" />
          </motion.div>
        </motion.div>
      ))}

      <div className="relative z-10 text-center px-6 max-w-6xl mx-auto">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="mb-10"
        >
          <img 
            src="https://i.postimg.cc/7YGQjLhq/logo-no-background-1.png" 
            alt="Advertising Pandit Logo" 
            className="mx-auto h-24 md:h-28"
            style={{ objectFit: 'contain' }}
          />
        </motion.div>

        <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="mb-6">
          <h2 className="text-2xl md:text-3xl font-light mb-2 text-purple-300">
            advertisingpandit.com
          </h2>
        </motion.div>

        <motion.h1 initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8, delay: 0.4 }} className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
          Turn Clicks Into Customers with{' '}
          <motion.span 
            className="bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text text-transparent"
            animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          >
            AI-Powered Marketing
          </motion.span>
        </motion.h1>

        <motion.p initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8, delay: 0.6 }} className="text-xl md:text-2xl mb-12 max-w-4xl mx-auto leading-relaxed text-purple-100">
          We help businesses scale faster with high-converting ad campaigns, engaging content, and cutting-edge AI automation.
        </motion.p>

        <motion.a
          href="https://form.rocketalgo.in/8303216"
          target="_blank"
          rel="noopener noreferrer"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="inline-block"
        >
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(255, 215, 0, 0.6)' }}
            whileTap={{ scale: 0.95 }}
            className="px-12 py-6 text-xl font-bold rounded-full transition-all duration-300 shadow-2xl"
            style={{ backgroundColor: '#FFD700', color: '#3a1c40' }}
          >
            <motion.span
              animate={{ textShadow: ["0 0 0px rgba(255, 215, 0, 0)", "0 0 10px rgba(255, 215, 0, 0.5)", "0 0 0px rgba(255, 215, 0, 0)"] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Get Your Free Strategy Plan
            </motion.span>
          </motion.button>
        </motion.a>
      </div>

      <motion.div 
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={{ y: [0, 10, 0], opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        <SafeIcon icon={FiChevronDown} className="text-3xl text-purple-300" />
      </motion.div>
    </section>
  );
};

export default HeroSection;